import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { FiPlus, FiTag, FiUser, FiMessageSquare, FiChevronLeft, FiInbox } from 'react-icons/fi';
import { useTicketStore } from '../../store/ticketStore';
import { useAuthStore } from '../../store/authStore';
import { useShallowStore } from '../../hooks/useShallowStore';
import api from '../../services/api';
import { toast } from 'react-toastify';
import TicketRow from './TicketRow.jsx';
import VirtualizedList from '../common/VirtualizedList.jsx';
import { SkeletonLine, SkeletonBlock, SkeletonCircle } from '../common/Skeleton.jsx';
import './Tickets.css';

const STATUS_FLOW = ['open', 'in_progress', 'ready_for_testing', 'ready_to_deploy', 'deployed', 'resolved', 'closed'];
const STATUS_LABELS = {
  open: 'Open', in_progress: 'In Progress', ready_for_testing: 'Ready for Testing',
  ready_to_deploy: 'Ready to Deploy', deployed: 'Deployed', resolved: 'Resolved', closed: 'Closed'
};
const MANAGER_ROLES = ['ceo', 'co_ceo', 'company_manager', 'sales_head', 'manager'];
const TICKET_ROW_HEIGHT = 56;

const ticketDetailSelector = (state) => ({
  fetchTicket: state.fetchTicket,
  currentTicket: state.currentTicket,
  updateStatus: state.updateStatus,
  assignTicket: state.assignTicket,
  addComment: state.addComment,
  loading: state.loading,
});

const ticketsListSelector = (state) => ({
  tickets: state.tickets,
  fetchTickets: state.fetchTickets,
  loading: state.loading,
});

const createTicketSelector = (state) => ({ createTicket: state.createTicket });

// Mirrors ticket-row-grid's 8 columns so the skeleton lines up pixel-for-pixel
// with the real rows once data arrives — no layout shift on load.
const TicketRowSkeleton = memo(function TicketRowSkeleton() {
  return (
    <div className="ticket-row ticket-row-grid" aria-hidden="true">
      <div className="ticket-cell"><SkeletonBlock width={64} height={18} radius={4} /></div>
      <div className="ticket-cell"><SkeletonLine width="80%" height={13} /></div>
      <div className="ticket-cell"><SkeletonLine width="70%" height={13} /></div>
      <div className="ticket-cell"><SkeletonLine width="70%" height={13} /></div>
      <div className="ticket-cell"><SkeletonBlock width={64} height={20} radius={12} /></div>
      <div className="ticket-cell"><SkeletonBlock width={90} height={20} radius={12} /></div>
      <div className="ticket-cell"><SkeletonLine width="65%" height={13} /></div>
      <div className="ticket-cell"><SkeletonLine width={70} height={13} /></div>
    </div>
  );
});

const TicketsTableSkeleton = memo(function TicketsTableSkeleton({ rows = 8 }) {
  return (
    <div className="tickets-table-wrapper" aria-hidden="true">
      <div className="tickets-table-head ticket-row-grid" role="row">
        <div className="ticket-header-cell">Ticket #</div>
        <div className="ticket-header-cell">Title</div>
        <div className="ticket-header-cell">Project</div>
        <div className="ticket-header-cell">Client</div>
        <div className="ticket-header-cell">Priority</div>
        <div className="ticket-header-cell">Status</div>
        <div className="ticket-header-cell">Assigned</div>
        <div className="ticket-header-cell">Created</div>
      </div>
      <div className="tickets-table-body">
        {Array.from({ length: rows }).map((_, i) => <TicketRowSkeleton key={i} />)}
      </div>
    </div>
  );
});

// Mirrors TicketDetail's layout (header badge/title/meta, main sections,
// sidebar info card) so the loading state doesn't jump around once real
// ticket data lands.
const TicketDetailSkeleton = memo(function TicketDetailSkeleton() {
  return (
    <div className="ticket-detail" aria-hidden="true">
      <div className="back-btn-placeholder">
        <SkeletonLine width={130} height={14} />
      </div>

      <div className="ticket-detail-header">
        <SkeletonBlock width={90} height={22} radius={6} style={{ marginBottom: 10 }} />
        <SkeletonLine width="45%" height={22} style={{ marginBottom: 12 }} />
        <div className="ticket-meta-row">
          <SkeletonBlock width={70} height={22} radius={12} />
          <SkeletonBlock width={100} height={22} radius={12} />
        </div>
      </div>

      <div className="ticket-detail-body">
        <div className="ticket-main">
          <section className="ticket-section">
            <SkeletonLine width={110} height={14} style={{ marginBottom: 14 }} />
            <SkeletonLine width="95%" height={12} style={{ marginBottom: 8 }} />
            <SkeletonLine width="80%" height={12} />
          </section>

          <section className="ticket-section">
            <SkeletonLine width={140} height={14} style={{ marginBottom: 14 }} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonBlock key={i} width={90} height={30} radius={20} />
              ))}
            </div>
          </section>

          <section className="ticket-section">
            <SkeletonLine width={130} height={14} style={{ marginBottom: 14 }} />
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <SkeletonCircle size={28} />
                <SkeletonLine width="60%" height={12} />
              </div>
            ))}
          </section>
        </div>

        <div className="ticket-sidebar">
          <div className="ticket-info-card">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="info-row">
                <SkeletonLine width={70} height={11} />
                <SkeletonLine width={90} height={11} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

function TicketDetail({ ticketId, onBack }) {
  const { fetchTicket, currentTicket, updateStatus, assignTicket, addComment, loading } = useShallowStore(useTicketStore, ticketDetailSelector);
  const { user } = useAuthStore();
  const [comment, setComment] = useState('');
  const [employees, setEmployees] = useState([]);
  const [showAssign, setShowAssign] = useState(false);

  useEffect(() => {
    fetchTicket(ticketId);
    if (MANAGER_ROLES.includes(user?.role)) {
      api.get('/users').then(r => setEmployees(r.data.users.filter(u => u.role !== 'client'))).catch(() => {});
    }
  }, [ticketId, fetchTicket, user?.role]);

  const handleStatusChange = useCallback(async (status) => {
    await updateStatus(ticketId, status);
    fetchTicket(ticketId);
  }, [updateStatus, fetchTicket, ticketId]);

  const handleAssign = useCallback(async (userId) => {
    await assignTicket(ticketId, userId);
    setShowAssign(false);
    fetchTicket(ticketId);
  }, [assignTicket, fetchTicket, ticketId]);

  const handleComment = useCallback(async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    await addComment(ticketId, comment);
    setComment('');
  }, [addComment, ticketId, comment]);

  const employeeOptions = useMemo(() => employees.map(emp => (
    <div
      key={emp._id}
      className="assign-option"
      onClick={() => handleAssign(emp._id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleAssign(emp._id); } }}
      role="button"
      tabIndex={0}
    >
      <div className="assign-avatar">{emp.firstName?.[0]}{emp.lastName?.[0]}</div>
      <span>{emp.firstName} {emp.lastName}</span>
      <small>{emp.role?.replace(/_/g, ' ')}</small>
    </div>
  )), [employees, handleAssign]);

  if (loading || !currentTicket) return <TicketDetailSkeleton />;

  const t = currentTicket;
  const canManage = MANAGER_ROLES.includes(user?.role);

  return (
    <div className="ticket-detail">
      <button className="back-btn" onClick={onBack}><FiChevronLeft /> Back to Tickets</button>

      <div className="ticket-detail-header">
        <div className="ticket-number-badge">{t.ticketNumber}</div>
        <h2>{t.title}</h2>
        <div className="ticket-meta-row">
          <span className={`priority-badge priority-${t.priority}`}>{t.priority}</span>
          <span className={`status-badge status-${t.status}`}>{STATUS_LABELS[t.status]}</span>
        </div>
      </div>

      <div className="ticket-detail-body">
        <div className="ticket-main">
          <section className="ticket-section">
            <h3>Description</h3>
            <p>{t.description || 'No description provided.'}</p>
          </section>

          {/* Status Update */}
          {canManage && (
            <section className="ticket-section">
              <h3>Update Status</h3>
              <div className="status-buttons">
                {STATUS_FLOW.map(s => (
                  <button
                    key={s}
                    className={`status-btn ${t.status === s ? 'active' : ''}`}
                    onClick={() => handleStatusChange(s)}
                    disabled={t.status === s}
                  >
                    {STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Comments */}
          <section className="ticket-section">
            <h3><FiMessageSquare /> Comments ({t.comments?.length || 0})</h3>
            <div className="comments-list">
              {t.comments?.length === 0 && <p className="empty-text">No comments yet.</p>}
              {t.comments?.map((c, i) => (
                <div key={c._id || i} className="comment-item">
                  <div className="comment-author">
                    <div className="comment-avatar">{c.author?.firstName?.[0]}{c.author?.lastName?.[0]}</div>
                    <span>{c.author?.firstName} {c.author?.lastName}</span>
                    <span className="comment-time">{new Date(c.createdAt).toLocaleString()}</span>
                  </div>
                  <p>{c.content}</p>
                </div>
              ))}
            </div>
            <form className="comment-form" onSubmit={handleComment}>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
              />
              <button type="submit" className="btn-primary" disabled={!comment.trim()}>Post Comment</button>
            </form>
          </section>

          {/* Activity */}
          <section className="ticket-section">
            <h3>Activity History</h3>
            <div className="activity-list">
              {t.activity?.map((a, i) => (
                <div key={a._id || i} className="activity-item">
                  <div className="activity-dot" />
                  <div className="activity-content">
                    <span className="activity-actor">{a.actor?.firstName} {a.actor?.lastName}</span>
                    <span className="activity-action"> {a.action.replace(/_/g, ' ')}</span>
                    {a.detail && <span className="activity-detail"> — {a.detail}</span>}
                    <span className="activity-time">{new Date(a.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="ticket-sidebar">
          <div className="ticket-info-card">
            <div className="info-row"><span>Project</span><strong>{t.project?.name}</strong></div>
            <div className="info-row"><span>Client</span><strong>{t.client?.firstName} {t.client?.lastName}</strong></div>
            <div className="info-row"><span>Created by</span><strong>{t.createdBy?.firstName} {t.createdBy?.lastName}</strong></div>
            <div className="info-row"><span>Created</span><strong>{new Date(t.createdAt).toLocaleDateString()}</strong></div>
            <div className="info-row">
              <span>Assigned to</span>
              <strong>{t.assignedTo ? `${t.assignedTo.firstName} ${t.assignedTo.lastName}` : 'Unassigned'}</strong>
            </div>
          </div>

          {canManage && (
            <div className="assign-section">
              <button className="btn-secondary full-width" onClick={() => setShowAssign(!showAssign)}>
                <FiUser /> {showAssign ? 'Cancel' : 'Assign Ticket'}
              </button>
              {showAssign && (
                <div className="assign-dropdown">
                  {employeeOptions}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateTicketModal({ onClose, onCreated }) {
  const { createTicket } = useShallowStore(useTicketStore, createTicketSelector);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', project: '', priority: 'medium' });

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.projects.filter(p => p.client))).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project) return toast.error('Select a project');
    const ticket = await createTicket(form);
    if (ticket) { onCreated(); onClose(); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Create Ticket</h2></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Project *</label>
            <select value={form.project} onChange={e => setForm({ ...form, project: e.target.value })} required>
              <option value="">Select project</option>
              {projects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="Ticket title" />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} placeholder="Describe the issue..." />
          </div>
          <div className="form-group">
            <label>Priority</label>
            <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary">Create Ticket</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TicketsList({ onSelectTicket }) {
  const { tickets, fetchTickets, loading } = useShallowStore(useTicketStore, ticketsListSelector);
  const { user } = useAuthStore();
  const [showCreate, setShowCreate] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');

  useEffect(() => {
    const params = {};
    if (filterStatus) params.status = filterStatus;
    if (filterPriority) params.priority = filterPriority;
    fetchTickets(params);
  }, [filterStatus, filterPriority, fetchTickets]);

  const canCreate = MANAGER_ROLES.includes(user?.role);

  const ticketRowKey = useCallback((t) => t._id, []);
  const renderTicketRow = useCallback((t, index, style) => (
    <TicketRow ticket={t} style={style} onSelect={onSelectTicket} />
  ), [onSelectTicket]);

  return (
    <div className="tickets-list-view">
      <div className="tickets-header">
        <h1><FiTag /> Tickets</h1>
        {canCreate && (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <FiPlus /> Create Ticket
          </button>
        )}
      </div>

      <div className="tickets-filters">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUS_FLOW.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
          <option value="">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
      </div>

      {loading ? (
        <TicketsTableSkeleton rows={8} />
      ) : tickets.length === 0 ? (
        <div className="tickets-empty">
          <FiInbox size={44} />
          <p>No tickets found.</p>
        </div>
      ) : (
        <div className="tickets-table-wrapper">
          <div className="tickets-table-head ticket-row-grid" role="row">
            <div className="ticket-header-cell" role="columnheader">Ticket #</div>
            <div className="ticket-header-cell" role="columnheader">Title</div>
            <div className="ticket-header-cell" role="columnheader">Project</div>
            <div className="ticket-header-cell" role="columnheader">Client</div>
            <div className="ticket-header-cell" role="columnheader">Priority</div>
            <div className="ticket-header-cell" role="columnheader">Status</div>
            <div className="ticket-header-cell" role="columnheader">Assigned</div>
            <div className="ticket-header-cell" role="columnheader">Created</div>
          </div>
          <div className="tickets-table-body" role="rowgroup">
            <VirtualizedList
              items={tickets}
              itemKey={ticketRowKey}
              rowHeight={TICKET_ROW_HEIGHT}
              renderRow={renderTicketRow}
              className="tickets-virtual-list scrollbar-thin"
              style={{ height: '100%' }}
            />
          </div>
        </div>
      )}

      {showCreate && (
        <CreateTicketModal
          onClose={() => setShowCreate(false)}
          onCreated={() => fetchTickets()}
        />
      )}
    </div>
  );
}

function TicketManagement() {
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  if (selectedTicketId) {
    return <TicketDetail ticketId={selectedTicketId} onBack={() => setSelectedTicketId(null)} />;
  }
  return <TicketsList onSelectTicket={setSelectedTicketId} />;
}

export default TicketManagement;
